import React, { useState } from 'react';
import { useTour } from '../contexts/TourContext';
import { useLocation } from 'react-router-dom';
import ReactJoyride, { CallBackProps } from 'react-joyride';
import { debugLog } from '../utils/debugLogger';
import './TourButton.css';

const routeToTourKeyMap: Record<string, string> = {
  '/dashboard': 'dashboard',
  '/applications': 'applications',
  '/resumes': 'resumes',
  '/profile': 'profile',
  '/settings': 'settings',
  '/job-search': 'jobSearch',
  '/agents': 'agents',
};

export const TourButton: React.FC = () => {
  const location = useLocation();
  const { startTour, isTourCompleted } = useTour();
  const [showTooltip, setShowTooltip] = useState(false);

  const currentTourKey = routeToTourKeyMap[location.pathname];
  const isCompleted = currentTourKey ? isTourCompleted(currentTourKey) : true;

  const handleClick = () => {
    if (currentTourKey) {
      startTour(currentTourKey);
    }
  };

  // Don't show button if there's no tour for this route
  if (!currentTourKey) {
    return null;
  }

  return (
    <button
      className="tour-button"
      onClick={handleClick}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      aria-label="Take a tour"
      title="Take a tour of this page"
      type="button"
    >
      <span className="tour-button-icon">?</span>
      {!isCompleted && <span className="tour-button-badge" />}
      {showTooltip && (
        <div className="tour-button-tooltip">
          {isCompleted ? 'Retake tour' : 'Take a tour'}
        </div>
      )}
    </button>
  );
};

export const TourOverlay: React.FC = () => {
  const { activeSteps, isRunning, finishTour, skipTour } = useTour();

  // #region agent log
  debugLog({location:'TourButton.tsx:TourOverlay:render',message:'TourOverlay rendering',data:{isRunning,stepsCount:activeSteps.length,stepsPreview:activeSteps.slice(0,2).map(s=>s.target)},hypothesisId:'C'});
  // #endregion

  console.log('[TourOverlay] Render - isRunning:', isRunning, 'steps:', activeSteps.length);

  if (!isRunning || activeSteps.length === 0) {
    return null;
  }

  console.log('[TourOverlay] Rendering Joyride with steps:', activeSteps);

  try {
    // #region agent log
    debugLog({location:'TourButton.tsx:TourOverlay:joyrideRender',message:'Rendering Joyride',data:{steps:activeSteps.map(s=>({target:s.target,hasContent:!!s.content}))},hypothesisId:'D,E'});
    // #endregion
    
    return (
      <ReactJoyride
        steps={activeSteps}
        run={isRunning}
        continuous
        showProgress
        showSkipButton
        disableScrolling={false}
        spotlightClicks={false}
        callback={(data: CallBackProps) => {
          console.log('[TourOverlay] Callback:', data.status, data.type);
          // #region agent log
          debugLog({location:'TourButton.tsx:TourOverlay:callback',message:'Joyride callback',data:{status:data.status,type:data.type,index:data.index},hypothesisId:'D'});
          // #endregion
          
          const { status } = data;
          if (status === 'finished') {
            finishTour();
          } else if (status === 'skipped') {
            skipTour();
          }
        }}
        styles={{
          options: {
            arrowColor: '#fff',
            backgroundColor: '#fff',
            overlayColor: 'rgba(0, 0, 0, 0.5)',
            primaryColor: '#3b82f6',
            textColor: '#1f2937',
            width: 380,
            zIndex: 10000,
          },
          tooltip: {
            borderRadius: '8px',
            fontSize: '14px',
          },
          tooltipContainer: {
            textAlign: 'left',
          },
          tooltipTitle: {
            fontSize: '16px',
            fontWeight: 600,
            marginBottom: '8px',
          },
          tooltipContent: {
            padding: '16px 0',
            lineHeight: '1.6',
          },
          buttonNext: {
            backgroundColor: '#3b82f6',
            borderRadius: '6px',
            fontSize: '14px',
            padding: '8px 16px',
          },
          buttonBack: {
            color: '#6b7280',
            fontSize: '14px',
            marginRight: '8px',
          },
          buttonSkip: {
            color: '#6b7280',
            fontSize: '14px',
          },
        }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip tour',
      }}
    />
  );
  } catch (error) {
    // #region agent log
    debugLog({location:'TourButton.tsx:TourOverlay:error',message:'Joyride render error',data:{error:error instanceof Error ? error.message : String(error)},hypothesisId:'D'});
    // #endregion
    console.error('[TourOverlay] Error rendering Joyride:', error);
    return null;
  }
};
